import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import { Maximize2, Minimize2, ArrowLeft } from "lucide-react";
import { useTranslation } from "../i18n";
import { logoutProcessor } from "../components/helpers/logoutProcessor";
import { playSuccess, warmUp as warmUpAudio } from "../components/helpers/audioPlayer";
import FlowLoader from "../components/FlowLoader";
import jliLogoWhite from "../images/jli-logo-white.svg";
import { apiFetch } from "../api/apiFetch";
import "../styles/display.css";

type CompleteChute = {
    axiomId: number;
    chute: string;
    palletId: string;
    system: string;
    timeStamp: string;
    tag: string;
    type: string;
    receivedItems: boolean;
    collected: boolean;
    processed: string;
};

// Format seconds into mm:ss or hh:mm:ss
const formatElapsed = (seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return `${m}:${String(s).padStart(2, "0")}`;
};

const DisplayCard = ({ chute }: { chute: CompleteChute }) => {
    const [elapsed, setElapsed] = useState(0);

    useEffect(() => {
        // Calculate initial elapsed from timestamp
        const created = new Date(chute.timeStamp).getTime();
        const now = Date.now();
        const initial = Math.max(0, Math.floor((now - created) / 1000));
        setElapsed(initial);

        const interval = setInterval(() => {
            setElapsed(Math.max(0, Math.floor((Date.now() - created) / 1000)));
        }, 1000);

        return () => clearInterval(interval);
    }, [chute.timeStamp]);

    const timeClass = elapsed >= 60 ? "display-card-red" : elapsed >= 30 ? "display-card-amber" : "";

    return (
        <div className={`display-card ${timeClass}`}>
            <div className="display-card-chute">{chute.chute.replace(/^(NP|PL)0*/i, "")}</div>
            <div className="display-card-meta">
                <span className="display-card-pallet">{chute.palletId}</span>
                <span className="display-card-timer">{formatElapsed(elapsed)}</span>
            </div>
        </div>
    );
};

const MIN_CARD_WIDTH = 400;
const COLUMN_GAP = 12;
const HYSTERESIS_PX = 20;
const POLL_MS = 1000;
const RESIZE_DEBOUNCE_MS = 100;
const SCALE_DEBOUNCE_MS = 150;
const MIN_SCALE = 0.55;

const CompleteChutesDisplay = () => {
    usePageTitle("complete_chutes");
    const { t } = useTranslation();
    const nav = useNavigate();

    const [chutes, setChutes] = useState<CompleteChute[]>([]);
    const [loading, setLoading] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [fullscreenSupported] = useState(() =>
        !!(document.fullscreenEnabled || (document as any).webkitFullscreenEnabled || (document as any).mozFullScreenEnabled)
    );

    const mainContentRef = useRef<HTMLDivElement>(null);
    const columnContainerRef = useRef<HTMLDivElement>(null);
    const colCountRef = useRef(1);
    const resizeTimerRef = useRef<NodeJS.Timeout | null>(null);
    const scaleTimerRef = useRef<NodeJS.Timeout | null>(null);
    const prevChuteLenRef = useRef(0);

    const userData = window.localStorage.getItem("user");
    const userObject = userData !== null ? JSON.parse(userData) : { warehouse: "Newport" };

    // Warm up audio on mount
    useEffect(() => { warmUpAudio(); }, []);

    // --- Sound: play once when new chute(s) added ---
    const prevChuteCountRef = useRef(0);
    const lastSoundTimeRef = useRef(0);

    useEffect(() => {
        if (loading) return;
        if (chutes.length > prevChuteCountRef.current && prevChuteCountRef.current > 0) {
            const now = Date.now();
            // Only play once per second even if multiple chutes arrive
            if (now - lastSoundTimeRef.current > 1000) {
                playSuccess();
                lastSoundTimeRef.current = now;
            }
        }
        prevChuteCountRef.current = chutes.length;
    }, [chutes, loading]);

    // --- Demo chutes for testing (remove when done) ---
    const DEMO_CHUTES = false;
    const demoIdRef = useRef(100);
    useEffect(() => {
        if (!DEMO_CHUTES) return;
        const now = Date.now();
        // Start with 3 chutes
        setChutes([
            { axiomId: 1, chute: "NP012", palletId: "PLT-4401", system: "S1", timeStamp: new Date(now - 10000).toISOString(), tag: "", type: "", receivedItems: true, collected: false, processed: "" },
            { axiomId: 2, chute: "NP045", palletId: "PLT-4402", system: "S1", timeStamp: new Date(now - 35000).toISOString(), tag: "", type: "", receivedItems: true, collected: false, processed: "" },
            { axiomId: 3, chute: "NP003", palletId: "PLT-4403", system: "S2", timeStamp: new Date(now - 75000).toISOString(), tag: "", type: "", receivedItems: true, collected: false, processed: "" },
        ]);
        prevChuteCountRef.current = 3;
        setLoading(false);

        // Add a new chute every 5 seconds
        const demoNames = ["NP078", "NP091", "NP022", "NP056", "NP008", "NP034", "NP067", "NP099", "NP041", "NP015"];
        const interval = setInterval(() => {
            const id = demoIdRef.current++;
            const idx = id % demoNames.length;
            setChutes(prev => [...prev, {
                axiomId: id,
                chute: demoNames[idx],
                palletId: `PLT-${4400 + id}`,
                system: "S1",
                timeStamp: new Date().toISOString(),
                tag: "", type: "", receivedItems: true, collected: false, processed: "",
            }]);
        }, 5000);

        return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- Data Fetching ---
    const fetchChutes = useCallback(async () => {
        if (DEMO_CHUTES) return;
        try {
            const res = await apiFetch(`/conveyor/chutes/complete/uncollected`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ warehouse: userObject.warehouse || "Newport" }),
            });
            if (res.ok) {
                const data = await res.json();
                setChutes(data);
            } else if (res.status === 401) {
                logoutProcessor();
                nav("/login");
            }
            // Silently ignore other errors — TV display retries automatically
        } catch {
            // Network error — silently retry next poll
        }
        setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nav]);

    useEffect(() => {
        fetchChutes();
        const interval = setInterval(fetchChutes, POLL_MS);
        return () => clearInterval(interval);
    }, [fetchChutes]);

    // --- Dynamic Column Count ---
    const recalcColumns = useCallback(() => {
        const container = mainContentRef.current;
        const colEl = columnContainerRef.current;
        if (!container || !colEl) return;

        const w = container.clientWidth;
        const idealCols = Math.max(1, Math.floor((w + COLUMN_GAP) / (MIN_CARD_WIDTH + COLUMN_GAP)));
        const current = colCountRef.current;

        let newCols = current;

        // Add column — need extra clearance
        if (idealCols > current) {
            const widthNeeded = current * (MIN_CARD_WIDTH + COLUMN_GAP) + MIN_CARD_WIDTH + HYSTERESIS_PX;
            if (w >= widthNeeded) newCols = idealCols;
        }
        // Remove column — need to drop further
        else if (idealCols < current) {
            const widthThreshold = (current - 1) * (MIN_CARD_WIDTH + COLUMN_GAP) + MIN_CARD_WIDTH - HYSTERESIS_PX;
            if (w < widthThreshold) newCols = idealCols;
        }

        if (newCols !== current) {
            colCountRef.current = newCols;
            colEl.style.columnCount = String(newCols);
            // Recalc scale after columns settle
            if (scaleTimerRef.current) clearTimeout(scaleTimerRef.current);
            scaleTimerRef.current = setTimeout(recalcScale, SCALE_DEBOUNCE_MS);
        }
    }, []);

    // --- Auto-Scale to Fit Viewport ---
    const recalcScale = useCallback(() => {
        const container = mainContentRef.current;
        const el = columnContainerRef.current;
        if (!container || !el) return;

        const availH = container.clientHeight;
        const availW = container.clientWidth;

        // Temporarily remove scale to measure true content size
        const prevTransform = el.style.transform;
        const prevWidth = el.style.width;
        const prevOverflow = el.style.overflow;

        el.style.transform = "";
        el.style.width = "";
        el.style.overflow = "visible";

        const contentH = el.scrollHeight;
        const contentW = el.scrollWidth;

        // Restore immediately (no visual flash — same frame)
        el.style.transform = prevTransform;
        el.style.width = prevWidth;
        el.style.overflow = prevOverflow;

        const scaleH = contentH > availH ? availH / contentH : 1;
        const scaleW = contentW > availW ? availW / contentW : 1;
        const scale = Math.min(scaleH, scaleW);

        if (scale < 1) {
            const clamped = Math.max(MIN_SCALE, scale);
            el.style.transform = `scale(${clamped})`;
            el.style.transformOrigin = "top left";
            el.style.width = `${100 / clamped}%`;
        } else {
            el.style.transform = "";
            el.style.transformOrigin = "";
            el.style.width = "";
        }

    }, []);

    // --- ResizeObserver ---
    useEffect(() => {
        const container = mainContentRef.current;
        if (!container) return;

        const observer = new ResizeObserver(() => {
            if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
            resizeTimerRef.current = setTimeout(() => {
                recalcColumns();
                if (scaleTimerRef.current) clearTimeout(scaleTimerRef.current);
                scaleTimerRef.current = setTimeout(recalcScale, SCALE_DEBOUNCE_MS);
            }, RESIZE_DEBOUNCE_MS);
        });

        observer.observe(container);
        return () => {
            observer.disconnect();
            if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
            if (scaleTimerRef.current) clearTimeout(scaleTimerRef.current);
        };
    }, [recalcColumns, recalcScale]);

    // Recalc when data changes
    useEffect(() => {
        if (chutes.length !== prevChuteLenRef.current) {
            prevChuteLenRef.current = chutes.length;
            // Let React render the new cards, then recalc
            requestAnimationFrame(() => {
                recalcColumns();
                setTimeout(recalcScale, SCALE_DEBOUNCE_MS);
            });
        }
    }, [chutes.length, recalcColumns, recalcScale]);

    // Initial column + scale calc after first render
    useEffect(() => {
        if (!loading) {
            requestAnimationFrame(() => {
                recalcColumns();
                setTimeout(recalcScale, SCALE_DEBOUNCE_MS);
            });
        }
    }, [loading, recalcColumns, recalcScale]);

    // --- Fullscreen ---
    const toggleFullScreen = useCallback(() => {
        const elem = document.documentElement as any;
        if (!isFullscreen) {
            if (elem.requestFullscreen) elem.requestFullscreen();
            else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen();
            else if (elem.mozRequestFullScreen) elem.mozRequestFullScreen();
        } else {
            const doc = document as any;
            if (doc.exitFullscreen) doc.exitFullscreen();
            else if (doc.webkitExitFullscreen) doc.webkitExitFullscreen();
            else if (doc.mozCancelFullScreen) doc.mozCancelFullScreen();
        }
    }, [isFullscreen]);

    useEffect(() => {
        const onChange = () => {
            const doc = document as any;
            const fs = !!(doc.fullscreenElement || doc.webkitFullscreenElement || doc.mozFullScreenElement);
            setIsFullscreen(fs);
            // Recalc after fullscreen change
            setTimeout(() => {
                recalcColumns();
                setTimeout(recalcScale, SCALE_DEBOUNCE_MS);
            }, 200);
        };

        document.addEventListener("fullscreenchange", onChange);
        document.addEventListener("webkitfullscreenchange", onChange);
        document.addEventListener("mozfullscreenchange", onChange);
        return () => {
            document.removeEventListener("fullscreenchange", onChange);
            document.removeEventListener("webkitfullscreenchange", onChange);
            document.removeEventListener("mozfullscreenchange", onChange);
        };
    }, [recalcColumns, recalcScale]);

    // --- Render ---
    return (
        <div className="display-root h-screen-safe">
            <header className="display-header">
                <img src={jliLogoWhite} alt="JLI Trading" className="display-logo" />
                <div className="display-header-right">
                    {fullscreenSupported && (
                        <button
                            className="display-fullscreen-btn"
                            onClick={toggleFullScreen}
                            title={isFullscreen ? t("exit_fullscreen") : t("enter_fullscreen")}
                        >
                            {isFullscreen ? <Minimize2 size={44} strokeWidth={2.5} /> : <Maximize2 size={44} strokeWidth={2.5} />}
                        </button>
                    )}
                    <button className="display-fullscreen-btn" onClick={() => nav("/admin/displays")}>
                        <ArrowLeft size={44} strokeWidth={2.5} />
                    </button>
                </div>
            </header>

            <main ref={mainContentRef} className="display-content">
                {loading && (
                    <div className="display-loading">
                        <FlowLoader />
                    </div>
                )}

                {!loading && chutes.length === 0 && (
                    <div className="display-loading">
                        <FlowLoader />
                    </div>
                )}

                {!loading && chutes.length > 0 && (() => {
                    const sorted = [...chutes].sort((a, b) =>
                        new Date(a.timeStamp).getTime() - new Date(b.timeStamp).getTime()
                    );
                    return (
                    <>
                        <div ref={columnContainerRef} className="display-columns">
                            {sorted.slice(0, 8).map((chute) => (
                                <DisplayCard key={chute.axiomId} chute={chute} />
                            ))}
                        </div>
                        {sorted.length > 8 && (
                            <div className="display-overflow-bar">
                                +{sorted.length - 8}
                            </div>
                        )}
                    </>
                    );
                })()}
            </main>

        </div>
    );
};

export default CompleteChutesDisplay;
